import { memo, useState, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';

import { NO_DATA } from './const';
import { ChartData } from './interface';
import ChartCircle from './chartCircle';
import ChartLabelInfo from './chartLabelInfo';
import ChartAmountInfo from './chartAmountInfo';

interface ActiveShapeProps {
  isHover: boolean;
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  value: number;
  name: string;
  totalLabel: string;
  totalAmount: number;
}

const useStyles = makeStyles(theme => ({
  ChartContainerDiv: {
    display: 'flex',
    // This is problem with rechart library, it only works when width = 99%
    width: '99%',
    [theme.breakpoints.up('md')]: {
      height: '75%'
    },
    [theme.breakpoints.down('md')]: {
      // in small screen the size of the chart should be bigger
      height: '600px',
      // due to problems in rechart library following line is needed
      // to avoid to big rechartWrapper generation in rechart library
      width: '60%',
      margin: 'auto'
    }
  },
  PieChartResponsiveContainer: {
    height: '100%',
    width: '100%'
  }
}));

const renderActiveShape = ({
  isHover,
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  value,
  name,
  totalLabel,
  totalAmount
}: ActiveShapeProps) => {
  if (name === NO_DATA) {
    return (
      <g>
        <ChartCircle cx={cx} cy={cy} r={outerRadius} />
        <ChartCircle cx={cx} cy={cy} r={innerRadius} />
        <ChartAmountInfo cx={cx} cy={cy} amount={0} />
        <ChartLabelInfo cx={cx} cy={cy} name={totalLabel} />
      </g>
    );
  }

  return (
    <g>
      <ChartAmountInfo cx={cx} cy={cy} amount={isHover ? value : totalAmount} />
      <ChartLabelInfo cx={cx} cy={cy} name={isHover ? name : totalLabel} />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cursor="pointer"
      />
    </g>
  );
};

interface Props {
  data: ChartData[];
  totalLabel: string;
  totalAmount: number;
}

const Chart = ({ data, totalLabel, totalAmount }: Props) => {
  const classes = useStyles();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isHover, setIsHover] = useState<boolean>(false);
  const handleOnChartEnter = useCallback((chartData: ChartData, index: number) => {
    setIsHover(true);
    setCurrentIndex(index);
  }, []);

  const handleOnChartLeave = useCallback(() => {
    setIsHover(false);
    setCurrentIndex(0);
  }, []);
  return (
    <div className={classes.ChartContainerDiv}>
      <ResponsiveContainer id="usage-summary-pie-chart" className={classes.PieChartResponsiveContainer}>
        <PieChart>
          <Pie
            activeIndex={currentIndex}
            activeShape={props => renderActiveShape({ ...props, isHover, totalLabel, totalAmount })}
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="100%"
            innerRadius="85%"
            paddingAngle={1}
            onMouseEnter={handleOnChartEnter}
            onMouseLeave={handleOnChartLeave}
            animationDuration={1500}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default memo(Chart);
